import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { SingleUnit } from './equipmentTypes';
import { EquipmentInfoRow, EquipmentSection } from './EquipmentFormShared';
import { EQUIPMENT_SEARCH_SUGGEST_FIELD_PROPS } from './equipmentSearchSuggestConfig';
import { ADD_EQUIPMENT_TYPE_OPTION_VALUE, PENDING_NEW_CATEGORY_ID, groupEquipmentOverviewFields } from './equipmentConstants';
import { DateField, ImageUpload, SearchSuggest, Select, SelectOrSearchSuggest, SearchSuggestActionOption } from '../ui';
import { UnitOfMeasureSearchSuggest } from './UnitOfMeasureSearchSuggest';
import { AddLocationModal } from '../regions/AddLocationModal';
import { ServiceCenterModal } from '../company-resources/ServiceCenterModal';
import {
    caretIndexAfterCurrencyFormat,
    coerceAcquisitionCost,
    formatAcquisitionCostForInput,
    formatCurrencyDraftDisplay,
    sanitizeCurrencyTyping,
} from './equipmentFieldUtils';
import { FieldFocusGuardConfig, FieldValidationConfig, UnitRecord } from './equipmentTypes';
import { UnitNameValidationError } from './equipmentUnitNameUtils';
import { applyEquipmentFieldCascade } from './equipmentAutosuggestOptions';
import { EquipmentDownReasonSection } from './EquipmentDownReasonSection';
import { EquipmentEditHistorySection } from './EquipmentEditHistorySection';
import { ReplacedCounterEditor } from './ReplacedCounterEditor';
import { useUserContext } from '../../contexts/UserContext';
import { hasPermission, Permission } from '../../utils/permissions';
import { formatNoteStamp, getUnitNotes } from '../../utils/unitNotes';

const UnitNotesEditor = ({
    unit,
    formData,
    setFormData,
}: {
    unit: SingleUnit;
    formData: Partial<SingleUnit>;
    setFormData: (data: Partial<SingleUnit>) => void;
}) => {
    const { companyUser, companyOwner } = useUserContext();
    const actorName = companyUser?.name || companyOwner?.name || 'Unknown';
    const history = getUnitNotes(unit.extraUnitInfo);
    const draft = String((formData as Record<string, unknown>).noteDraft ?? '');

    return (
        <div className="info-row editing">
            <label className="info-label" htmlFor="equipment-field-noteDraft">
                Notes
            </label>
            <span className="info-value-edit">
                {history ? <p className="unit-notes-log">{history}</p> : null}
                <p className="unit-notes-stamp">{formatNoteStamp(actorName)}</p>
                <textarea
                    id="equipment-field-noteDraft"
                    className="unit-notes-input"
                    rows={3}
                    value={draft}
                    placeholder="Add a note"
                    onChange={(event) =>
                        setFormData({
                            ...formData,
                            noteDraft: event.target.value,
                        } as Partial<SingleUnit>)
                    }
                />
            </span>
        </div>
    );
};

export const EquipmentOverviewTab = ({
    unit,
    isEditing,
    isNewUnit,
    priorityFields,
    formData,
    setFormData,
    sectionConfigs,
    fieldConfigs,
    autosuggestOptions,
    unitOfMeasureExtraValues = [],
    inspectionFormId,
    onInspectionFormIdChange,
    inspectionFormSelectVariant,
    inspectionFormActionOption,
    dailyServiceFormSelectVariant,
    dailyServiceFormActionOption,
    onCategoryIdChange,
    categorySelectVariant,
    categoryActionOption,
    pendingCategoryName,
    unitImageUrl,
    onImageSelect,
    isUploadingImage,
    imageUploadError,
    fieldValidations,
    categoryInputId = 'new-unit-categoryId',
    unitNameFocusGuard,
    companyId = '',
    reloadServiceCenters,
}: {
    unit: SingleUnit;
    isEditing: boolean;
    isNewUnit: boolean;
    priorityFields: string[];
    formData: Partial<SingleUnit>;
    setFormData: (data: Partial<SingleUnit>) => void;
    sectionConfigs: Array<{ key: string; title: string; editable: boolean }>;
    fieldConfigs: Record<
        string,
        Array<{
            key: string;
            label: string;
            type: 'input' | 'textarea' | 'custom' | 'select' | 'date' | 'search-suggest' | 'number' | 'toggle';
            editable?: boolean;
            dateMode?: 'date' | 'year';
            currency?: boolean;
            min?: number;
            hint?: string;
            getValue?: (unit: SingleUnit) => string;
            getLabel?: (unit: SingleUnit) => string;
            getOptions?: () => Array<{ label: string; value: string }>;
        }>
    >;
    autosuggestOptions: Record<string, Array<{ value: string; label: string }>>;
    unitOfMeasureExtraValues?: string[];
    inspectionFormId?: string;
    onInspectionFormIdChange?: (formId: string) => void;
    inspectionFormSelectVariant?: 'select' | 'search-suggest';
    inspectionFormActionOption?: SearchSuggestActionOption;
    dailyServiceFormSelectVariant?: 'select' | 'search-suggest';
    dailyServiceFormActionOption?: SearchSuggestActionOption;
    onCategoryIdChange?: (categoryId: string) => void;
    categorySelectVariant?: 'select' | 'search-suggest';
    categoryActionOption?: SearchSuggestActionOption;
    pendingCategoryName?: string | null;
    unitImageUrl?: string;
    onImageSelect?: (file: File) => void;
    isUploadingImage?: boolean;
    imageUploadError?: string | null;
    fieldValidations?: Record<string, FieldValidationConfig>;
    categoryInputId?: string;
    unitNameFocusGuard?: FieldFocusGuardConfig;
    companyId?: string;
    reloadServiceCenters?: () => void;
}) => {
    const { companyOwner, companyUser, businessAdmin } = useUserContext();
    const [addingLocation, setAddingLocation] = useState(false);
    const [addingServiceCenter, setAddingServiceCenter] = useState(false);
    const [pendingServiceCenterOption, setPendingServiceCenterOption] = useState<{
        value: string;
        label: string;
    } | null>(null);
    const canAddLocation =
        Boolean(companyId) &&
        isEditing &&
        hasPermission(companyOwner, companyUser, Permission.MANAGE_REGIONS, businessAdmin);
    const canAddServiceCenter =
        Boolean(companyId) &&
        isEditing &&
        hasPermission(companyOwner, companyUser, Permission.MANAGE_COMPANY_RESOURCES, businessAdmin);

    // Drop the temporary service-center option once the real reload has landed
    // (the live field options now include it), so stale injected options don't
    // linger in state across repeated inline creates.
    const serviceCenterLiveOptions = fieldConfigs['assignment-organization']?.find(
        (field) => field.key === 'serviceCenterId',
    )?.getOptions?.();
    useEffect(() => {
        if (!pendingServiceCenterOption) return;
        const landed = serviceCenterLiveOptions?.some(
            (option) => option.value === pendingServiceCenterOption.value,
        );
        if (landed) setPendingServiceCenterOption(null);
    }, [pendingServiceCenterOption, serviceCenterLiveOptions]);

    return (
    <div className="overview-content">
        {sectionConfigs.map((section) => (
            <Fragment key={section.key}>
                <EquipmentSection title={section.title}>
                    {groupEquipmentOverviewFields(fieldConfigs[section.key] ?? []).map((group, groupIndex) => {
                        const fieldRows = group.fields.map((field) => {
                        const isFieldEditable =
                            isEditing && section.editable && field.editable !== false && field.type !== 'custom';
                        const displayUnit = (
                            isEditing ? { ...unit, ...formData } : unit
                        ) as SingleUnit;
                        const displayLabel = field.getLabel ? field.getLabel(displayUnit) : field.label;

                        if (isFieldEditable && field.key === 'categoryId' && onCategoryIdChange) {
                            const allOptions = field.getOptions?.() || [];
                            const basePickerOptions =
                                categorySelectVariant === 'search-suggest'
                                    ? allOptions.filter((option) => option.value !== ADD_EQUIPMENT_TYPE_OPTION_VALUE)
                                    : allOptions;
                            const categoryValue = (formData as Record<string, any>).categoryId || '';
                            const pickerOptions =
                                pendingCategoryName && categoryValue === PENDING_NEW_CATEGORY_ID
                                    ? [
                                          { value: PENDING_NEW_CATEGORY_ID, label: pendingCategoryName },
                                          ...basePickerOptions.filter(
                                              (option) => option.value !== PENDING_NEW_CATEGORY_ID,
                                          ),
                                      ]
                                    : basePickerOptions;

                            return (
                                <EditableCategoryRow
                                    key={field.key}
                                    className={
                                        isNewUnit && priorityFields.includes(field.key)
                                            ? 'priority-field-highlight'
                                            : ''
                                    }
                                    label={displayLabel}
                                    value={categoryValue}
                                    options={pickerOptions}
                                    variant={categorySelectVariant}
                                    actionOption={
                                        categorySelectVariant === 'search-suggest' ? categoryActionOption : undefined
                                    }
                                    inputId={categoryInputId}
                                    onChange={onCategoryIdChange}
                                    fieldValidation={fieldValidations?.categoryId}
                                    isPendingNewCategory={
                                        Boolean(pendingCategoryName && categoryValue === PENDING_NEW_CATEGORY_ID)
                                    }
                                    pendingCategoryName={pendingCategoryName}
                                />
                            );
                        }

                        if (isFieldEditable && field.key === 'inspectionFormId' && onInspectionFormIdChange) {
                            const formOptions = field.getOptions?.() || [];
                            return (
                                <div
                                    key={field.key}
                                    className={
                                        isNewUnit && priorityFields.includes(field.key)
                                            ? 'priority-field-highlight'
                                            : ''
                                    }
                                >
                                    <div className="info-row editing">
                                        <label className="info-label" htmlFor="equipment-inspectionFormId">
                                            {displayLabel}
                                        </label>
                                        <span className="info-value-edit">
                                            <SelectOrSearchSuggest
                                                variant={inspectionFormSelectVariant}
                                                options={formOptions}
                                                value={inspectionFormId || ''}
                                                onChange={onInspectionFormIdChange}
                                                placeholder="Search inspection forms..."
                                                selectPlaceholder="Select inspection form"
                                                actionOption={
                                                    inspectionFormSelectVariant === 'search-suggest'
                                                        ? inspectionFormActionOption
                                                        : undefined
                                                }
                                                inputId="equipment-inspectionFormId"
                                            />
                                        </span>
                                    </div>
                                </div>
                            );
                        }

                        if (
                            isFieldEditable &&
                            field.key === 'dailyServiceLogFormId' &&
                            dailyServiceFormSelectVariant === 'search-suggest'
                        ) {
                            const formOptions = field.getOptions?.() || [];
                            const dailyServiceFormId =
                                (formData as Record<string, any>).dailyServiceLogFormId ??
                                (unit as UnitRecord).dailyServiceLogFormId ??
                                '';
                            return (
                                <div
                                    key={field.key}
                                    className={
                                        isNewUnit && priorityFields.includes(field.key)
                                            ? 'priority-field-highlight'
                                            : ''
                                    }
                                >
                                    <div className="info-row editing">
                                        <label className="info-label" htmlFor="equipment-dailyServiceLogFormId">
                                            {displayLabel}
                                        </label>
                                        <span className="info-value-edit">
                                            <SelectOrSearchSuggest
                                                variant="search-suggest"
                                                options={formOptions}
                                                value={dailyServiceFormId || ''}
                                                onChange={(val) =>
                                                    setFormData({ ...formData, dailyServiceLogFormId: val })
                                                }
                                                placeholder="Search daily service forms..."
                                                selectPlaceholder="Select daily service form"
                                                actionOption={dailyServiceFormActionOption}
                                                inputId="equipment-dailyServiceLogFormId"
                                            />
                                        </span>
                                    </div>
                                </div>
                            );
                        }

                        if (
                            isEditing &&
                            field.key === 'replacedUMCounterValue' &&
                            section.editable
                        ) {
                            return (
                                <ReplacedCounterEditor
                                    key={field.key}
                                    label={displayLabel}
                                    unit={unit}
                                    formData={formData}
                                    setFormData={setFormData}
                                />
                            );
                        }

                        if (isFieldEditable && field.key === 'notes') {
                            return (
                                <UnitNotesEditor
                                    key={field.key}
                                    unit={unit}
                                    formData={formData}
                                    setFormData={setFormData}
                                />
                            );
                        }

                        if (isFieldEditable) {
                            const fieldForRow =
                                field.key === 'serviceCenterId' &&
                                pendingServiceCenterOption &&
                                field.getOptions
                                    ? {
                                          ...field,
                                          getOptions: () => {
                                              const options = field.getOptions?.() ?? [];
                                              if (
                                                  options.some(
                                                      (option) =>
                                                          option.value === pendingServiceCenterOption.value,
                                                  )
                                              ) {
                                                  return options;
                                              }
                                              return [...options, pendingServiceCenterOption];
                                          },
                                      }
                                    : field;
                            return (
                                <div
                                    key={field.key}
                                    className={
                                        isNewUnit && priorityFields.includes(field.key)
                                            ? 'priority-field-highlight'
                                            : ''
                                    }
                                >
                                    <EditableInfoRow
                                        label={displayLabel}
                                        field={fieldForRow}
                                        value={
                                            (formData as Record<string, any>)[field.key] !== undefined
                                                ? (formData as Record<string, any>)[field.key]
                                                : ((unit as UnitRecord)[field.key] ?? null)
                                        }
                                        onChange={(val) =>
                                            setFormData(
                                                applyEquipmentFieldCascade(
                                                    formData as Record<string, unknown>,
                                                    field.key,
                                                    val,
                                                ) as Partial<SingleUnit>,
                                            )
                                        }
                                        formData={formData}
                                        setFormData={setFormData}
                                        autosuggestOptions={autosuggestOptions[field.key] || []}
                                        unit={unit}
                                        unitOfMeasureExtraValues={unitOfMeasureExtraValues}
                                        fieldValidation={fieldValidations?.[field.key]}
                                        focusBlockedBy={
                                            isNewUnit && field.key === 'name' ? unitNameFocusGuard : undefined
                                        }
                                        actionOption={
                                            canAddLocation && field.key === 'locationId'
                                                ? {
                                                      label: '+ Add location',
                                                      onClick: () => setAddingLocation(true),
                                                  }
                                                : canAddServiceCenter && field.key === 'serviceCenterId'
                                                  ? {
                                                        label: '+ Add service center',
                                                        onClick: () => setAddingServiceCenter(true),
                                                    }
                                                  : undefined
                                        }
                                    />
                                </div>
                            );
                        }

                        // Special handling for photo thumbnail — show upload in edit mode
                        if (isEditing && field.key === 'image' && onImageSelect) {
                            const imageLabelId = `${field.key}-label`;
                            return (
                                <div className="info-row editing photo-thumbnail-row" key={field.key}>
                                    <span className="info-label" id={imageLabelId}>
                                        {displayLabel}
                                    </span>
                                    <div className="info-value-edit">
                                        <ImageUpload
                                            previewUrl={unitImageUrl}
                                            isUploading={isUploadingImage}
                                            onSelect={onImageSelect}
                                            previewAlt={`${displayLabel} preview`}
                                            inputAriaLabel={
                                                unitImageUrl
                                                    ? `Change ${displayLabel.toLowerCase()}`
                                                    : `Upload ${displayLabel.toLowerCase()}`
                                            }
                                            labelId={imageLabelId}
                                        />
                                        {imageUploadError && (
                                            <p className="field-inline-error" role="alert">
                                                {imageUploadError}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <EquipmentInfoRow
                                key={field.key}
                                label={displayLabel}
                                value={
                                    field.getValue
                                        ? field.getValue(displayUnit)
                                        : (() => {
                                              const raw = (displayUnit as UnitRecord)[field.key];
                                              // Keep numeric 0 visible — `|| '-'` would hide it.
                                              return raw != null && raw !== '' ? String(raw) : '-';
                                          })()
                                }
                            />
                        );
                        });
                        if (group.kind === 'engine') {
                            return (
                                <div
                                    key={`${section.key}-engine`}
                                    className="equipment-engine-fields"
                                    role="group"
                                    aria-labelledby={`${section.key}-engine-legend`}
                                >
                                    <p id={`${section.key}-engine-legend`} className="equipment-engine-fields__legend">
                                        Engine
                                    </p>
                                    {fieldRows}
                                </div>
                            );
                        }
                        return <Fragment key={`${section.key}-fields-${groupIndex}`}>{fieldRows}</Fragment>;
                    })}
                </EquipmentSection>
                {section.key === 'daily-service-summary' && <EquipmentDownReasonSection unit={unit} />}
            </Fragment>
        ))}
        {!isEditing && !isNewUnit && (
            <EquipmentEditHistorySection
                editHistoryJson={(unit as UnitRecord).editHistory as string | null | undefined}
            />
        )}
        {addingLocation && companyId ? (
            <AddLocationModal
                companyId={companyId}
                isOpen
                onClose={() => setAddingLocation(false)}
                onCreated={async (location) => {
                    setAddingLocation(false);
                    if (!location?.id) return;
                    setFormData({
                        ...formData,
                        locationId: location.id,
                    } as Partial<SingleUnit>);
                }}
            />
        ) : null}
        {addingServiceCenter && companyId ? (
            <ServiceCenterModal
                isOpen
                companyId={companyId}
                editServiceCenter={null}
                onClose={() => setAddingServiceCenter(false)}
                onSave={async (saved) => {
                    setAddingServiceCenter(false);
                    if (!saved?.id) {
                        reloadServiceCenters?.();
                        return;
                    }
                    setPendingServiceCenterOption({
                        value: saved.id,
                        label: saved.name?.trim() || 'Service center',
                    });
                    setFormData({
                        ...formData,
                        serviceCenterId: saved.id,
                    } as Partial<SingleUnit>);
                    reloadServiceCenters?.();
                }}
            />
        ) : null}
    </div>
    );
};

const FieldInlineValidationError = ({
    fieldKey,
    error,
    onApplySuggestedName,
}: {
    fieldKey: string;
    error: UnitNameValidationError;
    onApplySuggestedName?: (name: string) => void;
}) => {
    if (error.type !== 'duplicate') {
        return (
            <p className="field-inline-error" id={`${fieldKey}-error`} role="alert">
                {error.message}
            </p>
        );
    }

    return (
        <p className="field-inline-error field-inline-error--unit-name" id={`${fieldKey}-error`} role="alert">
            <span>{`"${error.enteredName}" is already used by ${error.categoryName}.`}</span>
            <span>
                Suggested name:{' '}
                <button
                    type="button"
                    className="suggested-name"
                    onClick={() => onApplySuggestedName?.(error.suggestedName)}
                >
                    {error.suggestedName}
                </button>
            </span>
        </p>
    );
};

const EditableCategoryRow = ({
    label,
    value,
    options,
    variant,
    actionOption,
    inputId,
    onChange,
    fieldValidation,
    className,
    isPendingNewCategory = false,
    pendingCategoryName,
}: {
    label: string;
    value: string;
    options: Array<{ label: string; value: string }>;
    variant?: 'select' | 'search-suggest';
    actionOption?: SearchSuggestActionOption;
    inputId: string;
    onChange: (value: string) => void;
    fieldValidation?: FieldValidationConfig;
    className?: string;
    isPendingNewCategory?: boolean;
    pendingCategoryName?: string | null;
}) => {
    const valueRef = useRef(value);
    valueRef.current = value;

    const runFieldValidation = (rawValue: string): UnitNameValidationError | null => {
        if (!fieldValidation) return null;
        const error = fieldValidation.validate(rawValue);
        fieldValidation.onValidationError(error);
        return error;
    };

    const handleBlockedBlur = () => {
        if (!fieldValidation?.blockFocusUntilValid) return;
        const error = runFieldValidation(valueRef.current);
        if (error) {
            requestAnimationFrame(() => document.getElementById(inputId)?.focus());
        }
    };

    const handleBlockedKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLButtonElement>) => {
        if (!fieldValidation?.blockFocusUntilValid || event.key !== 'Tab') return;
        const error = fieldValidation.validate(valueRef.current);
        if (error) {
            event.preventDefault();
            fieldValidation.onValidationError(error);
        }
    };

    const handleChange = (nextValue: string) => {
        valueRef.current = nextValue;
        if (fieldValidation?.error) {
            fieldValidation.onValidationError(null);
        }
        onChange(nextValue);
    };

    const pendingNoteId = `${inputId}-pending-note`;

    const categoryFormatDisplayValue = (resolvedValue: string) => {
        if (isPendingNewCategory && pendingCategoryName && resolvedValue === PENDING_NEW_CATEGORY_ID) {
            return pendingCategoryName;
        }
        return options.find((option) => option.value === resolvedValue)?.label ?? resolvedValue;
    };

    return (
        <div
            className={[
                className,
                isPendingNewCategory ? 'equipment-category-field--pending' : '',
            ]
                .filter(Boolean)
                .join(' ')}
        >
            <div className="info-row editing">
                <label className="info-label" htmlFor={inputId}>
                    {label}
                </label>
                <span className="info-value-edit">
                    <SelectOrSearchSuggest
                        variant={variant}
                        options={options}
                        value={value}
                        onChange={handleChange}
                        placeholder="Search equipment categories..."
                        selectPlaceholder="Select equipment category"
                        actionOption={actionOption}
                        inputId={inputId}
                        hasError={!!fieldValidation?.error}
                        ariaDescribedBy={
                            [
                                fieldValidation?.error ? `${inputId}-error` : null,
                                isPendingNewCategory && pendingCategoryName ? pendingNoteId : null,
                            ]
                                .filter(Boolean)
                                .join(' ') || undefined
                        }
                        formatDisplayValue={
                            isPendingNewCategory ? categoryFormatDisplayValue : undefined
                        }
                        onBlur={handleBlockedBlur}
                        onInputKeyDown={handleBlockedKeyDown}
                        onTriggerKeyDown={handleBlockedKeyDown}
                    />
                    {fieldValidation?.error && (
                        <FieldInlineValidationError fieldKey={inputId} error={fieldValidation.error} />
                    )}
                    {isPendingNewCategory && pendingCategoryName && (
                        <p
                            className="equipment-category-pending-note"
                            id={pendingNoteId}
                            role="status"
                            aria-live="polite"
                        >
                            <span className="equipment-category-pending-badge">New category</span>
                            <span className="equipment-category-pending-text">
                                “{pendingCategoryName}” will be created when you save this unit.
                            </span>
                        </p>
                    )}
                </span>
            </div>
        </div>
    );
};

const CurrencyAmountInput = ({
    inputRef,
    value,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    hasError,
    ariaDescribedBy,
    ariaLabel,
    inputId,
    ariaLabelledBy,
}: {
    inputRef: React.RefObject<HTMLInputElement>;
    value: unknown;
    onChange: (val: number | null) => void;
    onFocus: () => void;
    onBlur: () => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    hasError?: boolean;
    ariaDescribedBy?: string;
    ariaLabel: string;
    inputId?: string;
    ariaLabelledBy?: string;
}) => {
    const [draft, setDraft] = useState(() => formatAcquisitionCostForInput(value));
    const draftRef = useRef(draft);
    const isFocusedRef = useRef(false);
    const pendingCaretRef = useRef<number | null>(null);

    useEffect(() => {
        if (isFocusedRef.current || document.activeElement === inputRef.current) return;
        const next = formatAcquisitionCostForInput(value);
        draftRef.current = next;
        setDraft(next);
    }, [value, inputRef]);

    useLayoutEffect(() => {
        if (pendingCaretRef.current == null || !inputRef.current) return;
        const caret = pendingCaretRef.current;
        pendingCaretRef.current = null;
        inputRef.current.setSelectionRange(caret, caret);
    }, [draft, inputRef]);

    const commitDraft = (raw: string) => {
        const coerced = coerceAcquisitionCost(raw);
        const formatted = formatAcquisitionCostForInput(coerced);
        draftRef.current = formatted;
        setDraft(formatted);
        onChange(coerced);
    };

    return (
        <div className={`currency-input${hasError ? ' has-error' : ''}`}>
            <span
                className="currency-input__prefix"
                aria-hidden="true"
                onMouseDown={(event) => {
                    event.preventDefault();
                    inputRef.current?.focus();
                }}
            >
                $
            </span>
            <input
                ref={inputRef}
                id={inputId}
                type="text"
                inputMode="decimal"
                value={draft}
                onChange={(event) => {
                    const input = event.target;
                    const caret = input.selectionStart ?? 0;
                    const sanitized = sanitizeCurrencyTyping(input.value);
                    if (sanitized === null) return;
                    const formatted = formatCurrencyDraftDisplay(sanitized);
                    pendingCaretRef.current = caretIndexAfterCurrencyFormat(
                        input.value.slice(0, caret),
                        formatted,
                    );
                    draftRef.current = formatted;
                    setDraft(formatted);
                    if (sanitized === '' || sanitized.endsWith('.')) {
                        if (sanitized === '') onChange(null);
                        return;
                    }
                    onChange(coerceAcquisitionCost(sanitized));
                }}
                onFocus={() => {
                    isFocusedRef.current = true;
                    onFocus();
                }}
                onBlur={() => {
                    isFocusedRef.current = false;
                    commitDraft(inputRef.current?.value ?? draftRef.current);
                    onBlur();
                }}
                onKeyDown={onKeyDown}
                className={hasError ? 'has-error' : undefined}
                aria-invalid={hasError ? true : undefined}
                aria-describedby={ariaDescribedBy}
                aria-labelledby={ariaLabelledBy}
                aria-label={ariaLabelledBy ? undefined : ariaLabel}
                autoComplete="off"
            />
        </div>
    );
};

const EditableInfoRow = ({
    label,
    field,
    value,
    onChange,
    formData,
    setFormData,
    autosuggestOptions,
    unitOfMeasureExtraValues = [],
    unit,
    fieldValidation,
    focusBlockedBy,
    actionOption,
}: {
    label: string;
    field: {
        key: string;
        type: string;
        dateMode?: 'date' | 'year';
        currency?: boolean;
        min?: number;
        hint?: string;
        getOptions?: () => Array<{ label: string; value: string }>;
    };
    value: any;
    onChange: (val: any) => void;
    formData: Partial<SingleUnit>;
    setFormData: (data: Partial<SingleUnit>) => void;
    autosuggestOptions: Array<{ value: string; label: string }>;
    unitOfMeasureExtraValues?: string[];
    unit: SingleUnit;
    fieldValidation?: FieldValidationConfig;
    focusBlockedBy?: FieldFocusGuardConfig;
    actionOption?: { label: string; onClick: () => void };
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const searchSuggestProps = EQUIPMENT_SEARCH_SUGGEST_FIELD_PROPS[field.key];
    const [blockBrowserAutofill, setBlockBrowserAutofill] = useState(true);

    const categoryId = String(
        (formData as Record<string, unknown>).categoryId || unit.categoryId || '',
    );
    const hasCategory = Boolean(categoryId);
    const manufacturerName = String(
        (formData as Record<string, unknown>).manufacturerName || unit.manufacturerName || '',
    );
    const engineManufacturer = String(
        (formData as Record<string, unknown>).engineManufacturer ||
            (unit as UnitRecord).engineManufacturer ||
            '',
    );

    const searchSuggestDisabled =
        (field.key === 'manufacturerName' && !hasCategory) ||
        (field.key === 'modelNumber' && !manufacturerName.trim()) ||
        (field.key === 'engineModelNumber' && !engineManufacturer.trim());

    const runFieldValidation = (rawValue: string): UnitNameValidationError | null => {
        if (!fieldValidation) return null;
        const error = fieldValidation.validate(rawValue);
        fieldValidation.onValidationError(error);
        return error;
    };

    const handleBlockedInputBlur = () => {
        if (!fieldValidation?.blockFocusUntilValid) return;
        const error = runFieldValidation(String(value ?? ''));
        if (error && inputRef.current) {
            requestAnimationFrame(() => inputRef.current?.focus());
        }
    };

    const handleBlockedInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!fieldValidation?.blockFocusUntilValid || event.key !== 'Tab') return;
        const error = fieldValidation.validate(String(value ?? ''));
        if (error) {
            event.preventDefault();
            fieldValidation.onValidationError(error);
        }
    };

    const handleApplySuggestedName = (suggestedName: string) => {
        onChange(suggestedName);
        fieldValidation?.onValidationError(null);
        requestAnimationFrame(() => inputRef.current?.focus());
    };

    const handleGuardedFocus = () => {
        if (!focusBlockedBy) return;
        const error = focusBlockedBy.validate();
        if (!error) return;
        inputRef.current?.blur();
        focusBlockedBy.onBlocked(error);
        requestAnimationFrame(() => document.getElementById(focusBlockedBy.refocusElementId)?.focus());
    };

    const handleTextInputChange = (nextValue: string) => {
        if (fieldValidation?.error) {
            fieldValidation.onValidationError(null);
        }
        onChange(nextValue);
    };

    const fieldDescribedBy = [
        fieldValidation?.error ? `${field.key}-error` : null,
        field.hint && !fieldValidation?.error ? `${field.key}-hint` : null,
    ]
        .filter(Boolean)
        .join(' ') || undefined;
    const fieldLabelId = `equipment-field-${field.key}-label`;
    const fieldControlId = `equipment-field-${field.key}`;
    const labelHtmlFor =
        field.type === 'date' || field.type === 'select' || field.type === 'toggle'
            ? undefined
            : fieldControlId;

    return (
        <div className="info-row editing">
            {labelHtmlFor ? (
                <label className="info-label" id={fieldLabelId} htmlFor={labelHtmlFor}>
                    {label}
                </label>
            ) : (
                <span className="info-label" id={fieldLabelId}>
                    {label}
                </span>
            )}
            <span className="info-value-edit">
                {field.type === 'search-suggest' && field.key === 'unitOfMeasure' ? (
                    <UnitOfMeasureSearchSuggest
                        value={value || ''}
                        onChange={onChange}
                        extraValues={unitOfMeasureExtraValues}
                        inputId={fieldControlId}
                    />
                ) : field.type === 'search-suggest' ? (
                    <SearchSuggest
                        options={autosuggestOptions}
                        value={value || ''}
                        onChange={onChange}
                        allowNew
                        disabled={searchSuggestDisabled}
                        disabledMessage={searchSuggestProps?.disabledMessage}
                        newLabel={searchSuggestProps?.newLabel ?? `+ Add ${label.toLowerCase()}`}
                        placeholder={searchSuggestProps?.placeholder ?? `Search ${label.toLowerCase()}...`}
                        inputId={fieldControlId}
                    />
                ) : field.type === 'toggle' ? (
                    <label className="toggle-switch">
                        <input
                            type="checkbox"
                            checked={!value}
                            onChange={(e) => {
                                const wantsArchive = e.target.checked;

                                // Enforce 30-day cooldown on archive status changes
                                const lastChange =
                                    (formData as Record<string, any>).lastArchiveStatusChange ||
                                    (unit as any).lastArchiveStatusChange;
                                if (lastChange) {
                                    const daysSinceChange =
                                        (Date.now() - new Date(lastChange).getTime()) / (1000 * 60 * 60 * 24);
                                    if (daysSinceChange < 30) {
                                        const daysRemaining = Math.ceil(30 - daysSinceChange);
                                        alert(
                                            `Archive status can only be changed once per month. Please wait ${daysRemaining} more day${daysRemaining > 1 ? 's' : ''}.`,
                                        );
                                        return;
                                    }
                                }

                                const today = new Date().toISOString().split('T')[0];

                                if (wantsArchive) {
                                    // Can't archive without removed from service date
                                    const rfsDate =
                                        (formData as Record<string, any>).removedFromServiceDate ||
                                        (unit as any).removedFromServiceDate;
                                    if (!rfsDate) {
                                        alert(
                                            'This unit must have a "Removed from Service Date" before it can be archived.',
                                        );
                                        return;
                                    }
                                    setFormData({
                                        ...formData,
                                        [field.key]: false,
                                        archivedDate: today,
                                        lastArchiveStatusChange: today,
                                    } as Partial<SingleUnit>);
                                    return;
                                }
                                // Unarchiving
                                setFormData({
                                    ...formData,
                                    [field.key]: true,
                                    archivedDate: null,
                                    lastArchiveStatusChange: today,
                                } as Partial<SingleUnit>);
                            }}
                        />
                        <span className="toggle-label">{value ? 'No' : 'Yes — Archived'}</span>
                    </label>
                ) : field.type === 'date' ? (
                    <DateField
                        value={value || ''}
                        onChange={onChange}
                        mode={field.dateMode ?? 'date'}
                    />
                ) : field.type === 'select' && field.getOptions ? (
                    <Select
                        options={field.getOptions()}
                        value={value || ''}
                        onChange={onChange}
                        placeholder="Select..."
                        ariaLabel={label}
                        ariaDescribedBy={fieldDescribedBy}
                        actionOption={actionOption}
                    />
                ) : field.type === 'textarea' ? (
                    <textarea
                        id={fieldControlId}
                        value={value || ''}
                        onChange={(event) => onChange(event.target.value)}
                        rows={3}
                        autoComplete="off"
                    />
                ) : field.type === 'number' && field.currency ? (
                    <CurrencyAmountInput
                        inputRef={inputRef}
                        value={value}
                        onChange={(next) => {
                            if (fieldValidation?.error) {
                                fieldValidation.onValidationError(null);
                            }
                            onChange(next);
                        }}
                        onFocus={handleGuardedFocus}
                        onBlur={handleBlockedInputBlur}
                        onKeyDown={handleBlockedInputKeyDown}
                        hasError={Boolean(fieldValidation?.error)}
                        ariaDescribedBy={fieldDescribedBy}
                        ariaLabel={label}
                        inputId={fieldControlId}
                        ariaLabelledBy={fieldLabelId}
                    />
                ) : field.type === 'number' ? (
                    <input
                        ref={inputRef}
                        id={fieldControlId}
                        type="number"
                        inputMode="decimal"
                        step="any"
                        value={value ?? ''}
                        onChange={(event) => {
                            const raw = event.target.value;
                            if (fieldValidation?.error) {
                                fieldValidation.onValidationError(null);
                            }
                            if (raw === '') {
                                onChange(null);
                                return;
                            }
                            const parsed = parseFloat(raw);
                            if (
                                field.min != null &&
                                !Number.isNaN(parsed) &&
                                parsed < field.min
                            ) {
                                fieldValidation?.onValidationError?.({
                                    type: 'required',
                                    message: `Enter ${field.min} or greater.`,
                                } as UnitNameValidationError);
                                return;
                            }
                            onChange(Number.isNaN(parsed) ? raw : parsed);
                        }}
                        onFocus={handleGuardedFocus}
                        onBlur={handleBlockedInputBlur}
                        onKeyDown={handleBlockedInputKeyDown}
                        min={field.min}
                        className={fieldValidation?.error ? 'has-error' : undefined}
                        aria-invalid={fieldValidation?.error ? true : undefined}
                        aria-describedby={fieldDescribedBy}
                        autoComplete="off"
                    />
                ) : (
                    <input
                        ref={inputRef}
                        id={fieldControlId}
                        name={`${fieldControlId}-no-autofill`}
                        value={value || ''}
                        onChange={(event) => handleTextInputChange(event.target.value)}
                        onFocus={(event) => {
                            if (blockBrowserAutofill) setBlockBrowserAutofill(false);
                            handleGuardedFocus();
                            event.currentTarget.select();
                        }}
                        onMouseDown={(event) => {
                            if (blockBrowserAutofill) {
                                setBlockBrowserAutofill(false);
                                event.currentTarget.readOnly = false;
                            }
                        }}
                        onBlur={handleBlockedInputBlur}
                        onKeyDown={handleBlockedInputKeyDown}
                        className={fieldValidation?.error ? 'has-error' : undefined}
                        aria-invalid={fieldValidation?.error ? true : undefined}
                        aria-describedby={fieldDescribedBy}
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        readOnly={blockBrowserAutofill}
                        data-lpignore="true"
                        data-1p-ignore="true"
                        data-form-type="other"
                    />
                )}
                {fieldValidation?.error && (
                    <FieldInlineValidationError
                        fieldKey={field.key}
                        error={fieldValidation.error}
                        onApplySuggestedName={
                            fieldValidation.error.type === 'duplicate' ? handleApplySuggestedName : undefined
                        }
                    />
                )}
                {field.hint && !fieldValidation?.error && (
                    <p className="equipment-field-hint" id={`${field.key}-hint`}>
                        {field.hint}
                    </p>
                )}
            </span>
        </div>
    );
};
